import { useEffect, useState } from 'react';

import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CloseCircleOutline, useToast } from '@aslanpay/portal-common-lib';

import useCreateIdentityProvider from 'app/hooks/useCreateIdentityProvider';
import useUpdateIdentityProvider from 'app/hooks/useUpdateIdentityProvider';

import { IdentityProviderModalProps } from './IdentityProviderModal.types';

function IdentityProviderModal({
  companyId = '',
  isOpen,
  onClose,
  identityProvider,
  onSuccess = () => {},
}: IdentityProviderModalProps) {
  const toast = useToast();
  const createMutation = useCreateIdentityProvider();
  const updateMutation = useUpdateIdentityProvider();

  const isEditMode = !!identityProvider;

  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTenantId(identityProvider?.tenantId ?? '');
      setClientId(identityProvider?.clientId ?? '');
    }
  }, [isOpen, identityProvider]);

  const trimmedTenantId = tenantId.trim();
  const trimmedClientId = clientId.trim();

  const hasRequiredFields = !!trimmedTenantId && !!trimmedClientId;
  const hasChanged =
    !isEditMode ||
    trimmedTenantId !== identityProvider?.tenantId ||
    trimmedClientId !== identityProvider?.clientId;
  const canSave = hasRequiredFields && hasChanged;

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    if (!companyId || !canSave) return;

    try {
      if (isEditMode && identityProvider) {
        await updateMutation.mutateAsync({
          companyId,
          id: identityProvider.id,
          request: { tenantId: trimmedTenantId, clientId: trimmedClientId },
        });
      } else {
        await createMutation.mutateAsync({
          companyId,
          request: { type: 'ENTRA_OIDC', tenantId: trimmedTenantId, clientId: trimmedClientId },
        });
      }

      toast({
        position: 'top-right',
        status: 'success',
        title: isEditMode ? 'Identity provider updated' : 'Identity provider added',
        description: isEditMode
          ? 'The SSO identity provider was successfully updated.'
          : 'The SSO identity provider was successfully added.',
      });

      onSuccess();
      onClose();
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: isEditMode
          ? 'Sorry, the identity provider could not be updated. Please try again.'
          : 'Sorry, the identity provider could not be added. Please try again.',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      {/* The shared Modal theme sets dialog maxWidth: 'unset'; an inline style is needed to cap it. */}
      <ModalContent style={{ maxWidth: 'min(560px, 90vw)' }}>
        <ModalHeader>
          <HStack>
            <Text as="h6">
              {isEditMode ? 'Edit SSO identity provider' : 'Add SSO identity provider'}
            </Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close identity provider modal"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <VStack spacing="20px" align="stretch">
            <Text textStyle="body" color="brand.mutedPurple">
              Microsoft Entra (OIDC). Enter the Directory (tenant) ID and Application (client) ID
              from the company&apos;s Entra app registration.
            </Text>

            <FormControl isRequired>
              <FormLabel>Tenant ID</FormLabel>
              <Input
                value={tenantId}
                onChange={(event) => setTenantId(event.target.value)}
                placeholder="e.g. 00000000-0000-0000-0000-000000000000"
                data-testid="idp-tenant-id-input"
              />
              <FormHelperText>The Entra Directory (tenant) ID.</FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Client ID</FormLabel>
              <Input
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
                placeholder="e.g. 00000000-0000-0000-0000-000000000000"
                data-testid="idp-client-id-input"
              />
              <FormHelperText>The Entra Application (client) ID.</FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing="10px">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              isLoading={isSaving}
              isDisabled={!canSave || isSaving}
              data-testid="save-identity-provider-button"
            >
              {isEditMode ? 'Save changes' : 'Add provider'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export { IdentityProviderModal };

export default IdentityProviderModal;
